import React from 'react';
import Link from '@components/atoms/link/link';
import extractFromContext from '@helpers/utils/extract-from-context';
import clsx from 'clsx';
import { defaultMetas } from '@constants/metas/default';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { usePreferences } from '@context/preferences.context';
import Head from '@components/atoms/head/head';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import VcImage from '@components/atoms/vc-images/vc-image';
import DefaultLayout from '@layouts/default-layout';
import { isConsignmentAllowedForCountry } from '@api/consignment';
import logger from '@helpers/utils/logger/client';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import { getAppInitialProps } from '@helpers/initial-props';
import styles from './index.module.scss';

type SellSecondHandPageProps = {
  eligible: boolean;
};

type ImageName = {
  name: string;
};

const sellImages: ImageName[] = [
  {
    name: 'productceline.jpg',
  },
  {
    name: 'product2.png',
  },
  {
    name: 'product3.jpg',
  },
  {
    name: 'product4.png',
  },
  {
    name: 'product5.png',
  },
];

const SellSecondHandPage: NextPageWithLayout<SellSecondHandPageProps> = (props): React.JSX.Element => {
  const { eligible: isConsignmentAvailable } = props;

  const { t } = useTranslation('sell-second-hand-clothes');
  const { country, language } = usePreferences();
  const pageMetas = defaultMetas[language];

  const whatToSellImage: string[] = sellImages.map((image: ImageName) => image.name);

  return (
    <>
      <Head metadata={pageMetas} />
      <article className={styles.sellerPage}>
        {/* sell header */}
        <header className={clsx(styles.header, 'innerContainer')}>
          <h2 className={clsx(styles.header__title, 'vc-title-l')}>{t('SELLER_PAGE.HEADER.TITLE')}</h2>
          {isConsignmentAvailable && (
            <div className={styles.header__actions}>
              <Link
                className={clsx(
                  styles.header__actions__button,
                  styles['btn-sized'],
                  'vc-btn',
                  'vc-btn--primary',
                  'vc-btn--medium',
                )}
                href="/sell-clothes-online/"
              >
                {t('SELLER_PAGE.HEADER.CTA_LIST')}
              </Link>
              <Link
                className={clsx(
                  styles.header__actions__button,
                  styles['btn-sized'],
                  'vc-btn',
                  'vc-btn--primary',
                  'vc-btn--medium',
                )}
                href={country === 'GB' ? t('LINKS.CONSIGNMENT.UK') : t('LINKS.CONSIGNMENT')}
              >
                {t('SELLER_PAGE.HEADER.CTA_CONSIGNMENT')}
              </Link>
            </div>
          )}
        </header>

        {/* sell how to */}
        <section className={styles.howToSellWrapper}>
          <div className={clsx(styles.howToSell, 'innerContainer')}>
            {isConsignmentAvailable && (
              <h3 className={clsx(styles.howToSell__title, 'vc-title-s')}>{t('SELLER_PAGE.HOW_TO_SELL.TITLE')}</h3>
            )}
            <ol className={styles.howToSell__list}>
              <li className={styles.howToSell__item}>
                <VcImage
                  src={`/images/sellerpage/seller-1-list-it.jpg`}
                  sizes="(min-width: 1024px) 380px,(min-width: 768px) 320px, 425px"
                  alt=""
                  shouldUseAssetImage
                  disableLazyLoad
                  fill
                  wrapperProps={{ className: styles.howToSell__item__photo }}
                />
                <div className={styles.howToSell__item__content}>
                  <h3 className={styles.howToSell__item__content__title}>
                    <span className={styles.howToSell__item__content__title__index}>1. </span>
                  </h3>
                  <div className={clsx(styles.howToSell__item__content__description, 'vc-body2')}>
                    {t('SELLER_PAGE.HOW_TO_SELL.ITEM_1.DESCRIPTION')}
                  </div>
                </div>
              </li>
              <li className={styles.howToSell__item}>
                <VcImage
                  src={`/images/sellerpage/seller-2-sell-it.jpg`}
                  sizes="(min-width: 1024px) 380px,(min-width: 768px) 320px, 425px"
                  alt=""
                  shouldUseAssetImage
                  disableLazyLoad
                  fill
                  wrapperProps={{ className: styles.howToSell__item__photo }}
                />
                <div className={styles.howToSell__item__content}>
                  <h3 className={styles.howToSell__item__content__title}>
                    <span className={styles.howToSell__item__content__title__index}>2. </span>
                  </h3>
                  <div className={clsx(styles.howToSell__item__content__description, 'vc-body2')}>
                    {t('SELLER_PAGE.HOW_TO_SELL.ITEM_2.DESCRIPTION')}
                  </div>
                </div>
              </li>
              <li className={styles.howToSell__item}>
                <VcImage
                  src={`/images/sellerpage/seller-3-get-paid.jpg`}
                  sizes="(min-width: 1024px) 380px,(min-width: 768px) 320px, 425px"
                  alt=""
                  shouldUseAssetImage
                  disableLazyLoad
                  fill
                  wrapperProps={{ className: styles.howToSell__item__photo }}
                />
                <div className={styles.howToSell__item__content}>
                  <h3 className={styles.howToSell__item__content__title}>
                    <span className={styles.howToSell__item__content__title__index}>3. </span>
                  </h3>
                  <div className={clsx(styles.howToSell__item__content__description, 'vc-body2')}>
                    {t('SELLER_PAGE.HOW_TO_SELL.ITEM_3.DESCRIPTION')}
                  </div>
                </div>
              </li>
            </ol>
            <Link
              className={clsx(
                styles.howToSell__callToAction,
                styles['btn-sized'],
                'vc-btn',
                'vc-btn--primary',
                'vc-btn--medium',
                'vc-btn--full--sm-down',
              )}
              href="/sell-clothes-online/"
            >
              {t('SELLER_PAGE.HOW_TO_SELL.CALL_TO_ACTION')}
            </Link>
          </div>
        </section>

        {/* sell consignement */}
        {isConsignmentAvailable && (
          <section className={clsx(styles.consignment, 'innerContainer')}>
            <VcImage
              src={`/images/sellerpage/seller-consignment.jpg`}
              sizes="(min-width: 1024px) 662px,(min-width: 768px) 768px, 425px"
              alt=""
              shouldUseAssetImage
              fill
              wrapperProps={{ className: styles.consignment__photo }}
            />
            <div className={styles.consignment__content}>
              <h3 className={clsx(styles.consignment__content__title, 'vc-title-s')}>
                {t('SELLER_PAGE.CONSIGNMENT.TITLE')}
              </h3>
              <p className={clsx(styles.consignment__content__description, 'vc-body2')}>
                {t('SELLER_PAGE.CONSIGNMENT.DESCRIPTION')}
              </p>
              <Link
                className={clsx(
                  styles.consignment__content__callToActio,
                  styles['btn-sized'],
                  'vc-btn',
                  'vc-btn--primary',
                  'vc-btn--medium',
                  'vc-btn--full--sm-down',
                )}
                href={country === 'GB' ? t('LINKS.CONSIGNMENT.UK') : t('LINKS.CONSIGNMENT')}
              >
                {t('SELLER_PAGE.QUESTIONS.CTA_CONSIGNMENT')}
              </Link>
            </div>
          </section>
        )}

        {/* sell why */}
        <section className={styles.whySellWrapper}>
          <div className={clsx(styles.whySell, 'innerContainer')}>
            <h3 className={clsx(styles.whySell__title, 'vc-title-s')}>{t('SELLER_PAGE.WHY.TITLE')}</h3>
            <div className={styles.whySell__content}>
              <VcImage
                src={`/images/sellerpage/seller-why-sell.jpg`}
                wrapperProps={{
                  className: styles.whySell__photo,
                }}
                width={142}
                height={142}
                alt=""
                shouldUseAssetImage
              />
              <div className={styles.whySell__item}>
                <VcImage
                  src={`/svg/world.svg`}
                  wrapperProps={{
                    className: styles.whySell__item__iconWorld,
                  }}
                  fill
                  alt=""
                  shouldUseAssetImage
                />
                <h4 className={styles.whySell__item__title}>{t('SELLER_PAGE.WHY.FAST.TITLE')}</h4>
                <div className={clsx(styles.whySell__item__description, 'vc-body2')}>
                  {t('SELLER_PAGE.WHY.FAST.DESCRIPTION')}
                </div>
              </div>
              <div className={styles.whySell__item}>
                <VcImage
                  src={`/svg/bag-other-one.svg`}
                  wrapperProps={{
                    className: styles.whySell__item__iconBag,
                  }}
                  fill
                  alt=""
                  shouldUseAssetImage
                />
                <h4 className={styles.whySell__item__title}>{t('SELLER_PAGE.WHY.FUNDS.TITLE')}</h4>
                <div className={clsx(styles.whySell__item__description, 'vc-body2')}>
                  {t('SELLER_PAGE.WHY.FUNDS.DESCRIPTION')}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* sell what */}
        <section className={styles.whatToSellWrapper}>
          <div className={clsx(styles.whatToSell, 'innerContainer')}>
            <h3 className={clsx(styles.whatToSell__title, 'vc-title-s')}>{t('SELLER_PAGE.WHAT_TO_SELL.TITLE')}</h3>
            <p className={clsx(styles.whatToSell__description, 'vc-body2')}>
              {t('SELLER_PAGE.WHAT_TO_SELL.DESCRIPTION')}
            </p>
            <ul className={styles.whatToSell__list}>
              {whatToSellImage &&
                whatToSellImage.map((item) => (
                  <li
                    key={item}
                    className={styles.whatToSell__list__item}
                  >
                    <VcImage
                      src={`/images/sellerpage/${item}`}
                      sizes="(min-width: 1024px) 154px, 113px"
                      alt=""
                      shouldUseAssetImage
                      fill
                      wrapperProps={{ className: styles.whatToSell__list__item__photo }}
                    />
                  </li>
                ))}
            </ul>
            <Link
              className={clsx(
                styles.whatToSell__callToAction,
                styles['btn-sized'],
                'vc-btn',
                'vc-btn--primary',
                'vc-btn--medium',
                'vc-btn--full--sm-down',
              )}
              href="/sell-clothes-online/"
            >
              {t('SELLER_PAGE.WHAT_TO_SELL.CALL_TO_ACTION')}
            </Link>
          </div>
        </section>

        {/* seller tools */}
        <section className={styles.sellerToolsWrapper}>
          <div className={clsx(styles.sellerTools, 'innerContainer')}>
            <h3 className={clsx(styles.sellerTools__title, 'vc-title-s')}>{t('SELLER_PAGE.TOOLS.TITLE')}</h3>
            <div className={styles.sellerTools__list}>
              <div className={styles.sellerTools__list__item}>
                <VcImage
                  src={`/svg/bell-light.svg`}
                  sizes="(min-width: 1024px) 250px, 48px"
                  alt=""
                  shouldUseAssetImage
                  fill
                  wrapperProps={{ className: styles.sellerTools__list__item__iconBell }}
                />
                <div className={styles.sellerTools__list__item__text}>
                  <div className={styles.sellerTools__list__item__title}>{t('SELLER_PAGE.TOOLS.ITEM1.TITLE')}</div>
                  <div className={clsx(styles.sellerTools__list__item__description, 'vc-body2')}>
                    {t('SELLER_PAGE.TOOLS.ITEM1.DESCRIPTION')}
                  </div>
                </div>
              </div>
              <div className={styles.sellerTools__list__item}>
                <VcImage
                  src={`/svg/communaute.svg`}
                  sizes="(min-width: 1024px) 250px, 77px"
                  alt=""
                  shouldUseAssetImage
                  fill
                  wrapperProps={{ className: styles.sellerTools__list__item__iconCommunity }}
                />
                <div className={styles.sellerTools__list__item__text}>
                  <div className={styles.sellerTools__list__item__title}>{t('SELLER_PAGE.TOOLS.ITEM2.TITLE')}</div>
                  <div className={clsx(styles.sellerTools__list__item__description, 'vc-body2')}>
                    {t('SELLER_PAGE.TOOLS.ITEM2.DESCRIPTION')}
                  </div>
                </div>
              </div>
              <div className={styles.sellerTools__list__item}>
                <VcImage
                  src={`/svg/offer.svg`}
                  sizes="(min-width: 1024px) 250px, 128px"
                  alt=""
                  shouldUseAssetImage
                  fill
                  wrapperProps={{ className: styles.sellerTools__list__item__iconOffer }}
                />
                <div className={styles.sellerTools__list__item__text}>
                  <div className={styles.sellerTools__list__item__title}>{t('SELLER_PAGE.TOOLS.ITEM3.TITLE')}</div>
                  <div className={clsx(styles.sellerTools__list__item__description, 'vc-body2')}>
                    {t('SELLER_PAGE.TOOLS.ITEM3.DESCRIPTION')}
                  </div>
                </div>
              </div>
            </div>
            <Link
              className={clsx(
                styles.sellerTools__callToAction,
                styles['btn-sized'],
                'vc-btn',
                'vc-btn--primary',
                'vc-btn--medium',
                'vc-btn--full--sm-down',
              )}
              href="/sell-clothes-online/"
            >
              {t('SELLER_PAGE.TOOLS.CALL_TO_ACTION')}
            </Link>
          </div>
        </section>

        {/* sell expertise */}
        <section className={clsx(styles.expertise, 'innerContainer')}>
          <div className={styles.expertise__photo}>
            <VcImage
              src={`/images/sellerpage/expertise.jpg`}
              sizes="(min-width: 768px) 768px, (min-width: 1024px) 425px,425px"
              alt=""
              shouldUseAssetImage
              fill
              wrapperProps={{ className: styles.expertise__photo__picture }}
            />
          </div>
          <div className={styles.expertise__content}>
            <VcImage
              src={`/svg/check-badge.svg`}
              alt=""
              shouldUseAssetImage
              width={57}
              height={57}
              wrapperProps={{ className: styles.expertise__content__icon }}
            />
            <h3 className={clsx(styles.expertise__content__title, 'vc-title-s')}>{t('SELLER_PAGE.EXPERTISE.TITLE')}</h3>
            <ol className={'vc-body2'}>
              {t('SELLER_PAGE.EXPERTISE.DESC')}
              <li className={styles.expertise__content__description}>{t('SELLER_PAGE.EXPERTISE.DESCRIPTION1')}</li>
              <li className={styles.expertise__content__description}>{t('SELLER_PAGE.EXPERTISE.DESCRIPTION2')}</li>
            </ol>
          </div>
        </section>

        {/* sell questions */}
        <section className={styles.questionsWrapper}>
          <div className={clsx(styles.questions, 'innerContainer')}>
            <h3 className={clsx(styles.questions__title, 'vc-title-xs')}>{t('SELLER_PAGE.QUESTIONS.TITLE')}</h3>
            <a
              className={clsx(styles.questions__faqLink)}
              href={t('SELLER_PAGE.QUESTIONS.FAQ.LINK')}
              rel="noopener"
            >
              {t('SELLER_PAGE.QUESTIONS.FAQ.TITLE')}
            </a>
            <div className={styles.questions__callToActionList}>
              <Link
                href="/sell-clothes-online/"
                className={clsx(
                  styles.questions__callToAction,
                  styles['btn-sized'],
                  'vc-btn',
                  'vc-btn--primary',
                  'vc-btn--medium',
                  'vc-btn--full--sm-down',
                )}
              >
                {t('SELLER_PAGE.QUESTIONS.CTA_SELL')}
              </Link>
              {isConsignmentAvailable && (
                <Link
                  className={clsx(
                    styles.questions__callToAction,
                    styles['btn-sized'],
                    'vc-btn',
                    'vc-btn--primary',
                    'vc-btn--medium',
                    'vc-btn--full--sm-down',
                  )}
                  href={country === 'GB' ? t('LINKS.CONSIGNMENT.UK') : t('LINKS.CONSIGNMENT')}
                >
                  {t('SELLER_PAGE.QUESTIONS.CTA_CONSIGNMENT')}
                </Link>
              )}
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

SellSecondHandPage.Layout = [DefaultLayout];
SellSecondHandPage.PageType = 'SellSecondHandPage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const { locale, serverAxiosRequestData } = extractFromContext(context);
    const initialProps = await getAppInitialProps(context);
    const [translations, eligible] = await Promise.all([
      serverSideTranslations(locale, ['default-layout', 'sell-second-hand-clothes', 'common']),
      isConsignmentAllowedForCountry(serverAxiosRequestData),
    ]);
    return {
      props: {
        ...initialProps,
        ...translations,
        eligible,
      },
    };
  } catch (err) {
    if (isRequestError(err)) {
      if (!isRequest404Error(err)) {
        logger.error(
          err,
          'There was an api request error on the Sell Second Hand Page - getServerSideProps - index.page.tsx',
        );
      }
      return {
        notFound: true,
      };
    }
    logger.error(err, 'The server failed to render the Sell Second Hand Page - getServerSideProps - index.page.tsx');
    throw err;
  }
};

export default SellSecondHandPage;
